import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type ActivePiece,
  type Board,
  type Cell,
  type PieceId,
  type Rotation,
  type RotationDirection,
} from "./types";

export const PIECE_NAMES: Readonly<Record<PieceId, "I" | "S" | "Z" | "O" | "T" | "L" | "J">> = {
  1: "I",
  2: "S",
  3: "Z",
  4: "O",
  5: "T",
  6: "L",
  7: "J",
};

export const PIECE_COLORS: Readonly<Record<PieceId | 8, string>> = {
  1: "#c90300",
  2: "#00af14",
  3: "#c238df",
  4: "#d3b42c",
  5: "#03a1e6",
  6: "#da8744",
  7: "#0035ed",
  8: "#8d8d8d",
};

export type PoseRows = readonly [string, string, string, string];

/** Exact 7 x 4 Golden Matrices recovered in the reproduction document. */
export const POSE_ROWS: Readonly<Record<PieceId, readonly [PoseRows, PoseRows, PoseRows, PoseRows]>> = {
  1: [
    ["..I.", "..I.", "..I.", "..I."],
    ["....", "....", "IIII", "...."],
    [".I..", ".I..", ".I..", ".I.."],
    ["....", "IIII", "....", "...."],
  ],
  2: [
    ["..S.", ".SS.", ".S..", "...."],
    ["....", ".SS.", "..SS", "...."],
    ["..S.", ".SS.", ".S..", "...."],
    ["....", ".SS.", "..SS", "...."],
  ],
  3: [
    [".Z..", ".ZZ.", "..Z.", "...."],
    ["....", "..ZZ", ".ZZ.", "...."],
    [".Z..", ".ZZ.", "..Z.", "...."],
    ["....", "..ZZ", ".ZZ.", "...."],
  ],
  4: [
    ["....", ".OO.", ".OO.", "...."],
    ["....", ".OO.", ".OO.", "...."],
    ["....", ".OO.", ".OO.", "...."],
    ["....", ".OO.", ".OO.", "...."],
  ],
  5: [
    ["....", ".TTT", "..T.", "...."],
    ["..T.", ".TT.", "..T.", "...."],
    ["..T.", ".TTT", "....", "...."],
    ["..T.", "..TT", "..T.", "...."],
  ],
  6: [
    ["....", ".LL.", "..L.", "..L."],
    ["....", "..L.", "LLL.", "...."],
    [".L..", ".L..", ".LL.", "...."],
    ["....", ".LLL", ".L..", "...."],
  ],
  7: [
    ["....", ".JJ.", ".J..", ".J.."],
    ["....", "JJJ.", "..J.", "...."],
    ["..J.", "..J.", ".JJ.", "...."],
    ["....", ".J..", ".JJJ", "...."],
  ],
};

export interface MatrixCell {
  /** Zero-based column inside the 4x4 matrix. */
  dx: number;
  /** Zero-based row inside the 4x4 matrix. */
  dy: number;
}

export interface BoardCell {
  /** One-based document board coordinate. */
  x: number;
  /** One-based document board coordinate. */
  y: number;
}

function compilePose(rows: PoseRows): readonly MatrixCell[] {
  const cells: MatrixCell[] = [];
  for (let dy = 0; dy < 4; dy += 1) {
    for (let dx = 0; dx < 4; dx += 1) {
      if (rows[dy][dx] !== ".") cells.push({ dx, dy });
    }
  }
  if (cells.length !== 4) throw new Error("Each legacy pose must contain exactly four cells");
  return cells;
}

export const POSE_CELLS: Readonly<Record<PieceId, readonly [readonly MatrixCell[], readonly MatrixCell[], readonly MatrixCell[], readonly MatrixCell[]]>> = {
  1: [compilePose(POSE_ROWS[1][0]), compilePose(POSE_ROWS[1][1]), compilePose(POSE_ROWS[1][2]), compilePose(POSE_ROWS[1][3])],
  2: [compilePose(POSE_ROWS[2][0]), compilePose(POSE_ROWS[2][1]), compilePose(POSE_ROWS[2][2]), compilePose(POSE_ROWS[2][3])],
  3: [compilePose(POSE_ROWS[3][0]), compilePose(POSE_ROWS[3][1]), compilePose(POSE_ROWS[3][2]), compilePose(POSE_ROWS[3][3])],
  4: [compilePose(POSE_ROWS[4][0]), compilePose(POSE_ROWS[4][1]), compilePose(POSE_ROWS[4][2]), compilePose(POSE_ROWS[4][3])],
  5: [compilePose(POSE_ROWS[5][0]), compilePose(POSE_ROWS[5][1]), compilePose(POSE_ROWS[5][2]), compilePose(POSE_ROWS[5][3])],
  6: [compilePose(POSE_ROWS[6][0]), compilePose(POSE_ROWS[6][1]), compilePose(POSE_ROWS[6][2]), compilePose(POSE_ROWS[6][3])],
  7: [compilePose(POSE_ROWS[7][0]), compilePose(POSE_ROWS[7][1]), compilePose(POSE_ROWS[7][2]), compilePose(POSE_ROWS[7][3])],
};

export const LEGACY_KICK_X = [0, -1, -2, 1, 2] as const;

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array<Cell>(BOARD_WIDTH).fill(0));
}

export function cloneBoard(board: readonly (readonly Cell[])[]): Board {
  return board.map((row) => [...row]);
}

export function isCell(value: unknown): value is Cell {
  return Number.isInteger(value) && typeof value === "number" && value >= 0 && value <= 8;
}

export function assertBoard(board: unknown): asserts board is Board {
  if (!Array.isArray(board) || board.length !== BOARD_HEIGHT) {
    throw new RangeError(`Board must have exactly ${BOARD_HEIGHT} rows`);
  }
  for (const row of board) {
    if (!Array.isArray(row) || row.length !== BOARD_WIDTH || !row.every(isCell)) {
      throw new RangeError(`Each board row must contain ${BOARD_WIDTH} cell values in 0...8`);
    }
  }
}

export function getPoseCells(active: ActivePiece): BoardCell[] {
  return POSE_CELLS[active.piece][active.rotation].map(({ dx, dy }) => ({
    x: active.x + dx,
    y: active.y + dy,
  }));
}

export function canPlace(board: readonly (readonly Cell[])[], active: ActivePiece): boolean {
  return getPoseCells(active).every(({ x, y }) => (
    x >= 1
    && x <= BOARD_WIDTH
    && y >= 1
    && y <= BOARD_HEIGHT
    && board[y - 1][x - 1] === 0
  ));
}

export function nextRotation(rotation: Rotation, direction: RotationDirection): Rotation {
  return (direction === "cw" ? (rotation + 1) % 4 : (rotation + 3) % 4) as Rotation;
}

/**
 * Original non-SRS rotation: test all horizontal offsets at the current y,
 * then repeat the same offsets one row lower. It never kicks upward.
 */
export function findLegacyRotation(
  board: readonly (readonly Cell[])[],
  active: ActivePiece,
  direction: RotationDirection,
): ActivePiece | null {
  const rotation = nextRotation(active.rotation, direction);
  for (const downOffset of [0, 1] as const) {
    for (const xOffset of LEGACY_KICK_X) {
      const candidate: ActivePiece = {
        piece: active.piece,
        rotation,
        x: active.x + xOffset,
        y: active.y + downOffset,
      };
      if (canPlace(board, candidate)) return candidate;
    }
  }
  return null;
}
